import { Modifier, type DragDropManager, type DragOperation, type Draggable, type Droppable } from "@dnd-kit/abstract";
import type { Coordinates } from "@dnd-kit/geometry";

import type { SnapGuide } from "../geometry/snapCalculation.ts";

export type SnapResult = {
  transform: Coordinates;
  guides: SnapGuide[];
};

export interface SnapToEdgesOptions {
  /**
   * Called on each drag frame to calculate the snapped transform.
   * Receives the current drag operation and returns the adjusted transform
   * and any active snap guide positions. Return undefined to skip snapping.
   */
  calculateSnap: (operation: DragOperation) => SnapResult | undefined;
  /**
   * Called with active snap guides (or undefined when drag ends/no snap).
   * Use this to update UI state for rendering guide lines.
   */
  onSnapChange?: (guides: SnapGuide[] | undefined) => void;
}

/**
 * Modifier that snaps drag transforms to nearby edges.
 * The actual snap logic is provided via the `calculateSnap` callback,
 * keeping this modifier generic and reusable across different contexts.
 *
 * `onSnapChange` is deduped: it fires only when the active guide set actually
 * changes (compared by `position` + `orientation`). Without this, the callback
 * fires on every drag frame and identical setState calls drive consumers'
 * `useSyncExternalStore` subscriptions enough to trip React's "many updates
 * inside startTransition" warning.
 */
export class SnapToEdges extends Modifier<DragDropManager<Draggable, Droppable>, SnapToEdgesOptions> {
  private lastGuideKey: string | undefined = undefined;

  private notifySnapChange(guides: SnapGuide[] | undefined): void {
    const key = guides ? guides.map((g) => `${g.orientation}:${g.position}`).join("|") : "";
    if (key === this.lastGuideKey) return;
    this.lastGuideKey = key;
    this.options?.onSnapChange?.(guides);
  }

  apply(operation: DragOperation): Coordinates {
    if (this.disabled || !this.options?.calculateSnap) return operation.transform;

    const result = this.options.calculateSnap(operation);
    if (!result) {
      this.notifySnapChange(undefined);
      return operation.transform;
    }

    this.notifySnapChange(result.guides.length > 0 ? result.guides : undefined);
    return result.transform;
  }
}
